// Dashboard.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { FaDollarSign, FaUsers, FaBriefcase, FaCar } from 'react-icons/fa';

const salesData = [
  { month: 'Ene', vida: 4000, salud: 2400, vehicular: 2400 },
  { month: 'Feb', vida: 3000, salud: 1398, vehicular: 2210 },
  { month: 'Mar', vida: 2000, salud: 9800, vehicular: 2290 },
  { month: 'Abr', vida: 2780, salud: 3908, vehicular: 2000 },
  { month: 'May', vida: 1890, salud: 4800, vehicular: 2181 },
  { month: 'Jun', vida: 2390, salud: 3800, vehicular: 2500 },
];

const latestSales = [
  { id: 1, client: "Juan Pérez", type: "Vida", amount: "$1,200", date: "2023-06-15" },
  { id: 2, client: "María García", type: "Salud", amount: "$800", date: "2023-06-14" },
  { id: 3, client: "Carlos Rodríguez", type: "Vehicular", amount: "$600", date: "2023-06-13" },
  { id: 4, client: "Ana Martínez", type: "Vida", amount: "$1,500", date: "2023-06-12" },
  { id: 5, client: "Luis Sánchez", type: "Salud", amount: "$950", date: "2023-06-11" },
];

const Dashboard = () => {
  const [totalSales, setTotalSales] = useState(0);
  const [newClients, setNewClients] = useState(0);
  const [vidaSales, setVidaSales] = useState(0);
  const [vehicularSales, setVehicularSales] = useState(0);

  const animateValue = (setValue, finalValue) => {
    let start = 0;
    const duration = 2000; // Duración de la animación en milisegundos
    const increment = finalValue / (duration / 20); // Incremento en cada paso
    const interval = setInterval(() => {
      start += increment;
      if (start >= finalValue) {
        clearInterval(interval);
        setValue(finalValue);
      } else {
        setValue(Math.floor(start));
      }
    }, 20); // Tiempo entre incrementos
  };

  useEffect(() => {
    animateValue(setTotalSales, 45231);
    animateValue(setNewClients, 2350);
    animateValue(setVidaSales, 1234);
    animateValue(setVehicularSales, 867);
  }, []);

  return (
    <Container>
      <CardGrid>
        <Card>
          <CardHeader>
            <CardTitle>Ventas Totales</CardTitle>
            <DollarSignIcon />
          </CardHeader>
          <CardContent>
            <CardAmount>${totalSales.toLocaleString()}</CardAmount>
            <CardSubtext>+20.1% del mes pasado</CardSubtext>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Nuevos Clientes</CardTitle>
            <UsersIcon />
          </CardHeader>
          <CardContent>
            <CardAmount>+{newClients.toLocaleString()}</CardAmount>
            <CardSubtext>+10.1% del mes pasado</CardSubtext>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Seguros de Vida Vendidos</CardTitle>
            <BriefcaseIcon />
          </CardHeader>
          <CardContent>
            <CardAmount>{vidaSales.toLocaleString()}</CardAmount>
            <CardSubtext>+2.5% del mes pasado</CardSubtext>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Seguros Vehiculares Vendidos</CardTitle>
            <CarIcon />
          </CardHeader>
          <CardContent>
            <CardAmount>{vehicularSales.toLocaleString()}</CardAmount>
            <CardSubtext>-0.3% del mes pasado</CardSubtext>
          </CardContent>
        </Card>
      </CardGrid>

      <ChartCard>
        <CardTitle>Tendencias de Ventas por Tipo de Seguro</CardTitle>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={salesData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="vida" fill="#4A90E2" name="Vida" />
            <Bar dataKey="salud" fill="#7ED321" name="Salud" />
            <Bar dataKey="vehicular" fill="#F5A623" name="Vehicular" />
            <Line type="monotone" dataKey="vida" stroke="#003049" name="Tendencia Vida" />
            <Line type="monotone" dataKey="salud" stroke="#028090" name="Tendencia Salud" />
            <Line type="monotone" dataKey="vehicular" stroke="#d62828" name="Tendencia Vehicular" />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>

      <TableCard>
        <CardTitle>Últimas Ventas</CardTitle>
        <Table>
          <thead>
            <TableRow>
              <TableHeader>Cliente</TableHeader>
              <TableHeader>Tipo de Seguro</TableHeader>
              <TableHeader>Monto</TableHeader>
              <TableHeader>Fecha</TableHeader>
            </TableRow>
          </thead>
          <tbody>
            {latestSales.map((sale) => (
              <TableRow key={sale.id}>
                <TableCell>{sale.client}</TableCell>
                <TableCell>{sale.type}</TableCell>
                <TableCell>{sale.amount}</TableCell>
                <TableCell>{sale.date}</TableCell>
              </TableRow>
            ))}
          </tbody>
        </Table>
      </TableCard>
    </Container>
  );
};

export default Dashboard;

// Estilos con styled-components
const Container = styled.div`
  width: -webkit-fill-available;
  height: auto;
  min-height: 79vh;
  max-height: 79vh;
  overflow-y: auto;
  max-width: calc(100vw - 84px);
  gap: 16px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin: 1% 16px 10% 1%;
  padding: 16px 16px 77px 16px;
  border-radius: 8px;
  justify-content: flex-start;
  &::-webkit-scrollbar {
    height: 4px;
    width: 3px;
    background-color: transparent !important;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #003049 !important;
    border-radius: 8px;
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: bold;
  color: #333;
  margin-bottom: 24px;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 24px;
  margin-bottom: 24px;
  width: 100%;
`;

const Card = styled.div`
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: 
      0px 3.200000047683716px 7.199999809265137px 0px rgba(0, 48, 72, 0.12), 
      0px 0.6000000238418579px 1.7999999523162842px 0px rgba(0, 48, 72, 0.10);
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  cursor: pointer;
  transition: 0.5s ease;
  &:hover {
  box-shadow: 
    0px 3.200000047683716px 7.199999809265137px 0px rgba(205, 18, 31, 0.12), 
    0px 0.6000000238418579px 1.7999999523162842px 0px rgba(205, 18, 31, 0.10);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CardTitle = styled.h2`
  font-size: 16px;
  font-weight: 500;
  color: #555;
`;

const CardContent = styled.div`
  margin-top: 12px;
`;

const CardAmount = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: #333;
`;

const CardSubtext = styled.p`
  font-size: 12px;
  color: #999;
`;

const DollarSignIcon = styled(FaDollarSign)`
  color: #4A90E2;
  font-size: 24px;
`;

const UsersIcon = styled(FaUsers)`
  color: #7ED321;
  font-size: 24px;
`;

const BriefcaseIcon = styled(FaBriefcase)`
  color: #50E3C2;
  font-size: 24px;
`;

const CarIcon = styled(FaCar)`
  color: #F5A623;
  font-size: 24px;
`;

const ChartCard = styled(Card)`
  margin-bottom: 24px;
  padding: 24px;
  width: -webkit-fill-available
`;

const TableCard = styled(Card)`
  padding: 24px;
  width: -webkit-fill-available;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 16px;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #eaeaea;
`;

const TableHeader = styled.th`
  text-align: left;
  padding: 12px;
  font-size: 14px;
  color: #333;
  font-weight: 500;
`;

const TableCell = styled.td`
  padding: 12px;
  font-size: 14px;
  color: #555;
`;
